import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, User, BookOpen, Newspaper, Sparkles, Search } from "lucide-react";
import api from "../lib/api";
import Seo from "../components/Seo";

const CATS = [
  { key: "all", label: "All", icon: BookOpen },
  { key: "blog", label: "Blog", icon: BookOpen },
  { key: "news", label: "News", icon: Newspaper },
  { key: "devotional", label: "Devotionals", icon: Sparkles },
];

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/posts");
        setPosts(data);
      } catch (e) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      if (cat !== "all" && p.category !== cat) return false;
      if (!q) return true;
      return (
        p.title?.toLowerCase().includes(q) ||
        p.excerpt?.toLowerCase().includes(q) ||
        p.tags?.some((t) => t.toLowerCase().includes(q))
      );
    });
  }, [posts, cat, query]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <main id="main-content" className="bg-[#050505] text-white" data-testid="blog-page">
      <Seo
        title="Blog & Devotionals"
        description="Pastor's blog, church news, and devotionals from The Push Center."
        path="/blog"
      />

      <section className="relative pt-20 pb-12 lg:pt-32 lg:pb-16 overflow-hidden">
        <div className="absolute inset-0 lightning-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-5">
            Words for the Journey
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
            Blog. News. <span className="italic text-electric text-pulse">Devotionals.</span>
          </h1>
          <p className="mt-7 text-lg text-zinc-300 max-w-2xl">
            Encouragement, updates, and devotionals from Pastor Demetrial Donshae Hill and The Push Center team.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-24">
        {/* FILTERS */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-10">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Post categories">
            {CATS.map((c) => (
              <button
                key={c.key}
                onClick={() => setCat(c.key)}
                role="tab"
                aria-selected={cat === c.key}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider transition ${
                  cat === c.key ? "bg-electric text-black" : "bg-[#0a0a0a] border border-white/10 text-zinc-400 hover:text-white"
                }`}
                data-testid={`blog-cat-${c.key}`}
              >
                <c.icon className="w-3.5 h-3.5" /> {c.label}
              </button>
            ))}
          </div>
          <div className="relative w-full lg:w-72">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" aria-hidden="true" />
            <label htmlFor="blog-search" className="sr-only">Search posts</label>
            <input
              id="blog-search"
              type="search"
              placeholder="Search posts..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 rounded-full bg-[#0a0a0a] border border-white/10 text-white placeholder-zinc-500 focus:outline-none focus:border-electric focus-visible:ring-2 focus-visible:ring-electric/40 transition"
              data-testid="blog-search-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="py-32 flex justify-center" data-testid="blog-loading">
            <div className="w-10 h-10 border-2 border-electric border-t-transparent rounded-full animate-spin" role="status" aria-label="Loading posts" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-24 text-center" data-testid="blog-empty">
            <BookOpen className="w-12 h-12 text-electric mx-auto mb-5" aria-hidden="true" />
            <h2 className="font-display font-bold text-2xl mb-3">No posts yet</h2>
            <p className="text-zinc-400 max-w-md mx-auto">
              {query || cat !== "all" ? "Try a different category or search." : "Check back soon — fresh words are on the way."}
            </p>
          </div>
        ) : (
          <>
            {/* FEATURED */}
            {featured && (
              <motion.article
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mb-14"
                data-testid="blog-featured"
              >
                <Link to={`/blog/${featured.slug}`} className="group block rounded-3xl overflow-hidden bg-[#0a0a0a] border border-white/5 hover:border-electric/50 transition">
                  <div className="grid lg:grid-cols-2 gap-0">
                    <div className="aspect-[4/3] lg:aspect-auto overflow-hidden">
                      {featured.cover_image ? (
                        <img src={featured.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-electric/20 via-[#0a0a0a] to-[#0a0a0a] flex items-center justify-center min-h-[300px]">
                          <BookOpen className="w-16 h-16 text-electric opacity-30" aria-hidden="true" />
                        </div>
                      )}
                    </div>
                    <div className="p-8 lg:p-12 flex flex-col justify-center">
                      <div className="flex items-center gap-3 text-xs uppercase tracking-wider mb-4">
                        <span className="px-3 py-1 rounded-full bg-electric/10 text-electric font-bold border border-electric/20">
                          {featured.category}
                        </span>
                        <span className="text-zinc-500 flex items-center gap-1.5">
                          <Calendar className="w-3 h-3" aria-hidden="true" />{fmt(featured.published_at)}
                        </span>
                      </div>
                      <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tighter mb-5 leading-[0.95]">
                        {featured.title}
                      </h2>
                      <p className="text-zinc-300 leading-relaxed mb-6">{featured.excerpt}</p>
                      <div className="flex items-center gap-2 text-sm text-zinc-400 mb-6">
                        <User className="w-3.5 h-3.5 text-electric" aria-hidden="true" />{featured.author}
                      </div>
                      <div className="inline-flex items-center gap-2 text-electric font-bold group-hover:gap-3 transition-all">
                        Read post <ArrowRight className="w-4 h-4" aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.article>
            )}

            {/* GRID */}
            {rest.length > 0 && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {rest.map((p, i) => (
                  <motion.article
                    key={p.id}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: i * 0.06 }}
                    data-testid={`blog-card-${i}`}
                  >
                    <Link to={`/blog/${p.slug}`} className="group block h-full rounded-2xl overflow-hidden bg-[#0a0a0a] border border-white/5 hover:border-electric/50 transition">
                      <div className="aspect-[16/10] overflow-hidden">
                        {p.cover_image ? (
                          <img src={p.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-electric/15 via-[#0a0a0a] to-[#0a0a0a] flex items-center justify-center">
                            <BookOpen className="w-10 h-10 text-electric opacity-30" aria-hidden="true" />
                          </div>
                        )}
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-3 text-xs uppercase tracking-wider mb-3">
                          <span className="px-2 py-0.5 rounded-full bg-electric/10 text-electric font-bold border border-electric/20 text-[10px]">
                            {p.category}
                          </span>
                          <span className="text-zinc-500 text-[10px]">{fmt(p.published_at)}</span>
                        </div>
                        <h3 className="font-display font-bold text-xl tracking-tight mb-2 line-clamp-2 group-hover:text-electric transition-colors">
                          {p.title}
                        </h3>
                        <p className="text-sm text-zinc-400 leading-relaxed line-clamp-3">{p.excerpt}</p>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}
